import { Extension } from "@tiptap/core"
import { Plugin } from '@tiptap/pm/state';
import Image from "@tiptap/extension-image"
import { TableCell } from "@tiptap/extension-table";

export const getImageFromClipboard = (event) => {
    if (!event.clipboardData) return []

    const items = event.clipboardData.items
    const files = []

    for (const item of items)
    {
        if (item.type.startsWith('image/'))
        {
            const file = item.getAsFile()
            if(file)
            {
                files.push(file)
            }
        }
    }

  return files
}

export const handleImagePaste = async (api, file, editor) => {
    if(!editor || !file) return;
    const uploadUuid = crypto.randomUUID()
    const objectUrl = URL.createObjectURL(file);

    editor
    .chain()
    .focus()
    .setImage({
        src: objectUrl,
        'data-upload-id': uploadUuid,
    })
    .run()
    
    try
    {
        const response = await api.uploadImage(file, uploadUuid);
        const imageUrl = response.url;
        
        editor.commands.command(({ tr, dispatch }) => {
            editor.state.doc.descendants((node, pos) => {
                if(node.type.name === 'image' && node.attrs['data-upload-id'] === uploadUuid)
                {
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        src: imageUrl,
                    });
                }
            })

            if (dispatch) dispatch(tr)
            return true
        })

        URL.revokeObjectURL(objectUrl)
    }
    catch(e)
    {
        console.error(e)
    }
}

export const CustomImage = Image.extend({
    addAttributes() {
        return {
        ...this.parent?.(),
        'data-upload-id': {
            default: null,
        },
        }
    },
});

export const TableCellUI = Extension.create({
  name: 'tableCellUI',

  addProseMirrorPlugins()
  {

    const editor = this.editor;

    return [
        new Plugin({
            view(editorView)
            {   
                const el = document.createElement('div')
                el.className = 'tableCellUI'

                el.innerHTML = `
                    <div class="zone top">+</div>
                    <div class="zone bottom">+</div>
                    <div class="zone left">+</div>
                    <div class="zone right">+</div>
                    <div class="zone colDelete">-</div>
                    <div class="zone rowDelete">-</div>
                `

                document.body.appendChild(el);

                const top = el.querySelector('.top')
                const bottom = el.querySelector('.bottom')
                const left = el.querySelector('.left')
                const right = el.querySelector('.right')
                const colDelete = el.querySelector('.colDelete')
                const rowDelete = el.querySelector('.rowDelete')

                const addRowBefore = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.addRowBefore();
                }

                const addRowAfter = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.addRowAfter();
                }

                const addColumnBefore = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.addColumnBefore();
                }

                const addColumnAfter = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.addColumnAfter();
                }

                const deleteColumn = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.deleteColumn();
                }

                const deleteRow = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    editor.commands.deleteRow();
                }

                top.addEventListener("click", addRowBefore);
                bottom.addEventListener("click", addRowAfter);
                left.addEventListener("click", addColumnBefore);
                right.addEventListener("click", addColumnAfter);
                colDelete.addEventListener("click", deleteColumn);
                rowDelete.addEventListener("click", deleteRow);

                const update = () => {
                    const { selection } = editorView.state
                    const cell = findCell(editorView, selection)

                    if(!cell)
                    {
                        el.style.display = 'none'
                        return
                    }

                    const rect = cell.getBoundingClientRect();

                    el.style.display = 'block'
                    el.style.position = 'fixed'
                    el.style.left = '0px'
                    el.style.top = '0px'

                    top.style.cssText = `
                        position:fixed;
                        left:${rect.left}px;
                        top:${rect.top - 13}px;
                        width:${rect.width}px;
                        height:10px;
                    `

                    bottom.style.cssText = `
                        position:fixed;
                        left:${rect.left}px;
                        top:${rect.bottom + 3}px;
                        width:${rect.width}px;
                        height:10px;
                    `

                    left.style.cssText = `
                        position:fixed;
                        left:${rect.left - 13}px;
                        top:${rect.top}px;
                        width:10px;
                        height:${rect.height}px;
                    `

                    right.style.cssText = `
                        position:fixed;
                        left:${rect.right + 3}px;
                        top:${rect.top}px;
                        width:10px;
                        height:${rect.height}px;
                    `

                    colDelete.style.cssText = `
                        position:fixed;
                        left:${rect.left}px;
                        top:${rect.top - 26}px;
                        width:${rect.width}px;
                        height:10px;
                    `

                    rowDelete.style.cssText = `
                        position:fixed;
                        left:${rect.left - 26}px;
                        top:${rect.top}px;
                        width:10px;
                        height:${rect.height}px;
                    `
                }

                const findCell = (view) => {
                    const { $from } = view.state.selection

                    for(let d = $from.depth; d > 0; d--)
                    {
                        const node = $from.node(d)

                        if( node.type.name === 'tableCell' || node.type.name === 'tableHeader')
                        {
                            const dom = view.nodeDOM($from.before(d))
                            return dom instanceof HTMLElement ? dom : null
                        }
                    }

                    return null
                }


          update()

          return {
            update,
            destroy() {
                el.remove();
                top.removeEventListener("click", addRowBefore);
                bottom.removeEventListener("click", addRowAfter);
                left.removeEventListener("click", addColumnBefore);
                right.removeEventListener("click", addColumnAfter);
                colDelete.removeEventListener("click", deleteColumn);
                rowDelete.removeEventListener("click", deleteRow);
            },
          }
        },
      }),
    ]
  },
})

export const isCellMerged = (editor) => {
    const { selection } = editor.state
    const { $from } = selection

    for (let d = $from.depth; d > 0; d--) {
        const node = $from.node(d)

        if (
        node.type.name === 'tableCell' ||
        node.type.name === 'tableHeader'
        ) {
        return node.attrs.colspan > 1 || node.attrs.rowspan > 1
        }
    }

    return false
}

export const isInTable = (editor) => {
    const { selection } = editor.state
    const { $from } = selection

    for (let d = $from.depth; d > 0; d--) {
        const node = $from.node(d)

        if (node.type.name === 'table') {
        return true
        }
    }

    return false
}

export const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            backgroundColor: {
                default: null,
                parseHTML: element => element.style.backgroundColor || null,
                renderHTML: attributes => {
                    if(!attributes.backgroundColor) return {};
                    return { style: 'background-color:' + attributes.backgroundColor }
                }
            }
        }
    }
})

export const getTableCellBackground = (editor) => {
  const { selection } = editor.state
  const { $from } = selection

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d)

    if (
      node.type.name === 'tableCell' ||
      node.type.name === 'tableHeader'
    ) {
      return node.attrs.backgroundColor ?? null
    }
  }

  return null
}

